import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Quote } from 'lucide-react';
import { getRandomMotivationalQuote } from '../firebase/subcollections';

const MotivationalQuote = () => {
  const [quote, setQuote] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadQuote();
  }, []);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const result = await getRandomMotivationalQuote();
      if (result.success) {
        setQuote(result.data);
      }
    } catch (error) {
      setQuote("The only way to do great work is to love what you do. - Steve Jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadQuote();
    } finally {
      setRefreshing(false);
    }
  };

  const quoteVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <div className="relative overflow-hidden h-full">
      {/* Enhanced background with animated gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 animate-gradient"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
      
      {/* Enhanced background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-6 right-6 text-7xl animate-pulse">💫</div>
        <div className="absolute bottom-6 left-6 text-5xl animate-bounce">✨</div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-8xl opacity-5">🌟</div>
      </div>
      
      <div className="relative z-10 p-6 sm:p-8 h-full flex flex-col">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg">
              <Quote className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Daily Motivation</h2>
              <p className="text-sm text-white/80">Fuel your inspiration</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-3 rounded-xl bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <RefreshCw className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Enhanced Quote Content */}
        <div className="flex-1 flex items-center justify-center">
          {loading ? (
            <div className="space-y-6 w-full max-w-lg">
              <div className="animate-pulse">
                <div className="h-8 bg-white/30 rounded-lg w-3/4 mb-4"></div>
                <div className="h-8 bg-white/30 rounded-lg w-1/2 mb-4"></div>
                <div className="h-8 bg-white/30 rounded-lg w-2/3"></div>
              </div>
            </div>
          ) : (
            <motion.div
              key={quote} // Force re-animation when quote changes
              variants={quoteVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="text-center space-y-8 w-full max-w-lg"
            >
              {/* Enhanced Quote Display */}
              <div className="relative">
                <div className="absolute -top-6 -left-6 text-6xl opacity-20">"</div>
                <blockquote className="text-xl sm:text-2xl lg:text-3xl leading-relaxed font-medium text-white px-6">
                  {quote.split(' - ')[0]}
                </blockquote>
                <div className="absolute -bottom-6 -right-6 text-6xl opacity-20">"</div>
              </div>
              
              {/* Enhanced Attribution */}
              {quote.includes(' - ') && (
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                  <cite className="text-lg sm:text-xl font-semibold text-white/90 italic">
                    — {quote.split(' - ')[1]}
                  </cite>
                  <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="mt-8 pt-8 border-t border-white/20">
          <div className="text-center">
            <p className="text-base text-white/80 font-medium mb-4">
              Click the refresh button for a new dose of motivation!
            </p>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-3 h-3 bg-white/40 rounded-full animate-pulse"></div>
              <div className="w-3 h-3 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-3 h-3 bg-white/40 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotivationalQuote;
